/**
 * i18n module: tenant creation wizard (TenantWizardPage + wizard/* steps).
 * Namespace: `wizard`.
 */

export const de = {
  heading: 'Neuen Mandanten erstellen',
  stepIndicatorAriaLabel: 'Wizard-Schritte',
  contentAriaLabelDefault: 'Wizard-Schritt',
  unknownError: 'Unbekannter Fehler bei der Erstellung.',
  steps: {
    basic: 'Grundeinstellungen',
    apps: 'Anwendungen',
    mail: 'Mail & MTA',
    iam: 'IAM',
    network: 'Netzwerk',
    storage: 'Storage',
    quota: 'Quota',
    security: 'Sicherheit',
    license: 'Lizenz',
    preview: 'Vorschau',
  },
  nav: {
    cancel: 'Abbrechen',
    back: 'Zurück',
    next: 'Weiter',
  },
  cancelModal: {
    title: 'Wizard abbrechen?',
    body: 'Ungespeicherte Änderungen gehen verloren. Möchten Sie den Wizard wirklich abbrechen?',
    confirm: 'Ja, abbrechen',
  },
  progress: {
    steps: {
      namespace: 'Namespace erstellen',
      rbac: 'RBAC konfigurieren',
      networkPolicies: 'NetworkPolicies anwenden',
      secrets: 'Secrets erstellen',
      keycloakClient: 'Keycloak-Client registrieren',
      postgres: 'PostgreSQL-Datenbanken erstellen',
      redis: 'Redis bereitstellen',
      nextcloud: 'Nextcloud installieren',
      oxAppSuite: 'OX App Suite installieren',
      elementMatrix: 'Element/Matrix installieren',
      openproject: 'OpenProject installieren',
      collabora: 'Collabora installieren',
      jitsi: 'Jitsi installieren',
      xwiki: 'XWiki installieren',
      ingress: 'Ingress konfigurieren',
      tls: 'TLS-Zertifikate ausstellen',
      activate: 'Mandant aktivieren',
    },
    titlePrefix: 'Mandant erstellen:',
    badgeCompleted: 'Abgeschlossen',
    badgeError: 'Fehler',
    badgePending: 'In Bearbeitung',
    percentSuffix: '%',
    ariaLabelPercentPrefix: 'Bereitstellungsfortschritt:',
    timelineAriaLabel: 'Bereitstellungsschritte',
    errorLabel: 'Fehler:',
    completeMessage: 'Der Mandant wurde erfolgreich bereitgestellt.',
    retry: 'Wiederholen',
    retrying: 'Wiederholung...',
    close: 'Schließen',
    viewTenant: 'Mandant anzeigen',
    liveRegionCreated: 'Mandant erfolgreich erstellt.',
    liveRegionErrorPrefix: 'Fehler:',
    sseConnectionLost: 'Verbindung zum Server verloren. Bitte versuchen Sie es erneut.',
  },
  basicInfo: {
    sectionTitle: 'Grundeinstellungen',
    cluster: 'Cluster',
    tenantName: 'Mandantenname',
    tenantNameHint: 'Kleinbuchstaben, Ziffern und Bindestriche (3-63 Zeichen).',
    domain: 'Domain',
    domainHint: 'Vollqualifizierter Domainname (z.B. tenant.example.de).',
    displayName: 'Anzeigename',
    displayNameHint: 'Optionaler Anzeigename für den Mandanten.',
    edition: 'Edition',
    editionAriaLabel: 'Edition auswählen',
    editionCE: 'Community Edition (CE)',
    editionCEAriaLabel: 'Community Edition (CE)',
    editionCEDesc: 'Open-Source-Komponenten ohne Enterprise-Funktionen.',
    editionEE: 'Enterprise Edition (EE)',
    editionEEAriaLabel: 'Enterprise Edition (EE)',
    editionEEDesc: 'Erweiterte Funktionen, SLA-Support und Enterprise-Integrationen.',
    adminEmail: 'Admin-E-Mail',
    adminEmailHint: 'E-Mail-Adresse des Mandanten-Administrators.',
    release: 'openDesk-Version',
    releaseLoading: 'Lade Releases...',
    errors: {
      tenantNameRequired: 'Mandantenname ist erforderlich.',
      tenantNameInvalid: 'Nur Kleinbuchstaben, Ziffern und Bindestriche (3-63 Zeichen).',
      domainRequired: 'Domain ist erforderlich.',
      domainInvalid: 'Bitte geben Sie einen gültigen FQDN ein.',
      adminEmailRequired: 'Admin-E-Mail ist erforderlich.',
      adminEmailInvalid: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
    },
  },
  iam: {
    keycloakTitle: 'Keycloak',
    keycloakDescription: 'Identity und Access Management Konfiguration für den Mandanten.',
    ldapOU: 'LDAP OU',
    ldapOUHint: 'Organisational Unit im LDAP-Verzeichnis (leer = automatisch).',
    ldapBaseDN: 'LDAP Base DN (optional)',
    ldapBaseDNHint: 'Leer lässt den Cluster-Standard gelten.',
    mfaTitle: 'Multi-Faktor-Authentifizierung',
    mfaDescription: 'Aktivierte MFA-Methoden für Benutzer dieses Mandanten.',
    mfaLegend: 'MFA-Methoden auswählen',
    mfaGroupAriaLabel: 'MFA-Methoden',
    mfaTotpDescription: 'Zeitbasierte Einmalpasswörter (Google Authenticator, etc.).',
    mfaWebauthnDescription: 'FIDO2/WebAuthn Hardware-Sicherheitsschlüssel.',
    mfaSmsDescription: 'Einmalpasswörter per SMS (erfordert SMS-Gateway).',
    errors: {
      ldapOUInvalid: String.raw`LDAP OU darf keine Sonderzeichen enthalten (,=+<>#;\").`,
    },
  },
  mail: {
    smtpTitle: 'SMTP-Konfiguration',
    smtpDescription: 'Konfigurieren Sie den ausgehenden Mailserver für diesen Mandanten.',
    smtpHost: 'SMTP-Host',
    smtpHostHint: 'Hostname des SMTP-Servers (z.B. smtp.example.de).',
    smtpPort: 'SMTP-Port',
    smtpPortHint: 'Standard: 587 (STARTTLS) oder 465 (TLS).',
    credentialRef: 'Credential-Secret',
    credentialRefHint: 'Name des Kubernetes Secrets mit SMTP-Zugangsdaten (optional).',
    dkimTitle: 'DKIM',
    dkimDescription: 'DomainKeys Identified Mail für E-Mail-Authentifizierung.',
    dkimSelector: 'DKIM-Selektor',
    dkimSelectorHint: 'DNS-Selektor für DKIM-Signatur (z.B. default).',
    errors: {
      smtpHostInvalid: 'Bitte geben Sie einen gültigen Hostnamen ein.',
      smtpPortInvalid: 'Port muss zwischen 1 und 65535 liegen.',
    },
  },
  network: {
    isolationTitle: 'Netzwerk-Isolierung',
    isolationDescription: 'Legt fest, wie streng der Netzwerkverkehr zwischen Mandanten isoliert wird.',
    isolationLegend: 'Isolierungsmodus',
    isolationAriaLabel: 'Isolierungsmodus auswählen',
    isolationStrict: 'Strikt',
    isolationStrictAriaLabel: 'Strikt',
    isolationStrictDesc: 'Kein Netzwerkverkehr zwischen Mandanten-Namespaces. Empfohlen für Produktion.',
    isolationPermissive: 'Permissiv',
    isolationPermissiveAriaLabel: 'Permissiv',
    isolationPermissiveDesc: 'Netzwerkverkehr zwischen Mandanten-Namespaces erlaubt. Nur für Entwicklung.',
    webrtcTitle: 'WebRTC-Server',
    webrtcDescription: 'STUN/TURN-Server für Jitsi-Videokonferenzen und Echtzeitkommunikation.',
    stunServer: 'STUN-Server',
    stunServerHint: 'z.B. stun:stun.example.de:3478',
    turnServer: 'TURN-Server',
    turnServerHint: 'z.B. turn:turn.example.de:3478',
    errors: {
      stunInvalid: 'Bitte geben Sie eine gültige STUN-Server-Adresse ein.',
      turnInvalid: 'Bitte geben Sie eine gültige TURN-Server-Adresse ein.',
    },
  },
  storage: {
    s3Title: 'Object Storage (S3)',
    s3Description: 'S3-kompatible Speicherkonfiguration pro Anwendung.',
    appNextcloud: 'Nextcloud',
    appNextcloudDesc: 'Dateispeicher und Zusammenarbeit.',
    appMigrations: 'Migrationen',
    appMigrationsDesc: 'Backup- und Migrationsdaten.',
    bucket: 'Bucket',
    bucketHintPrefix: 'S3-Bucket für',
    endpoint: 'Endpunkt',
    endpointHint: 'S3-API-Endpunkt (z.B. s3.example.de).',
    useSSL: 'SSL verwenden',
    useSSLAriaLabelPrefix: 'SSL für',
    useSSLAriaLabelSuffix: 'verwenden',
    pathStyle: 'Path-Style',
    pathStyleAriaLabelPrefix: 'Path-Style für',
    pathStyleAriaLabelSuffix: 'verwenden',
    dbTitle: 'Datenbank',
    dbDescription: 'PostgreSQL-Verbindungskonfiguration für den Mandanten.',
    dbHost: 'Datenbank-Host',
    dbHostHint: 'PostgreSQL-Host (z.B. postgresql.opendesk.svc:5432).',
    dbSecret: 'Datenbank-Secret',
    dbSecretHint: 'Kubernetes Secret mit Datenbank-Zugangsdaten.',
    errors: {
      endpointInvalid: 'Bitte geben Sie einen gültigen Endpunkt ein.',
      dbHostInvalid: 'Bitte geben Sie einen gültigen Datenbank-Hostnamen ein.',
    },
  },
  quota: {
    resourcesTitle: 'Ressourcen-Quota',
    resourcesDescription: 'Maximal zuteilbare Ressourcen für diesen Mandanten.',
    cpu: 'CPU Kerne',
    cpuHint: '1 - 256 Kerne',
    memory: 'Arbeitsspeicher (GiB)',
    memoryHint: '1 - 1024 GiB',
    storage: 'Speicher (GiB)',
    storageHint: '10 - 10.000 GiB',
    pods: 'Max. Pods',
    podsHint: '10 - 10.000',
    replicasTitle: 'Replicas pro Chart',
    replicasDescription: 'Überschreiben Sie die Standard-Replikanzahl für einzelne Helm-Charts.',
    chart: 'Chart',
    chartPlaceholder: 'Chart auswählen...',
    replicas: 'Replicas',
    removeEntryPrefix: 'Replica-Eintrag',
    removeEntrySuffix: 'entfernen',
    remove: 'Entfernen',
    addReplica: '+ Replica-Eintrag',
    errors: {
      cpuRange: 'CPU muss zwischen 1 und 256 Kernen liegen.',
      memoryRange: 'Arbeitsspeicher muss zwischen 1 und 1024 GiB liegen.',
      storageRange: 'Speicher muss zwischen 10 und 10.000 GiB liegen.',
      podsRange: 'Pod-Limit muss zwischen 10 und 10.000 liegen.',
      chartRequired: 'Chart-Name ist erforderlich.',
      replicasRange: 'Replicas muss zwischen 0 und 99 liegen.',
    },
  },
  apps: {
    legend: 'Komponenten auswählen',
    selectAll: 'Alle auswählen',
    deselectAll: 'Alle abwählen',
    groupAriaLabel: 'Komponenten des Release',
    loading: 'Lade Komponenten ...',
    loadError: 'Komponenten konnten nicht geladen werden.',
    errors: {
      apps: 'Mindestens eine Komponente muss aktiviert sein.',
    },
  },
  resources: {
    profileLabel: 'Skalierungsprofil',
    profileSmall: 'Klein (4 CPU, 8 GiB RAM, 50 GiB)',
    profileMedium: 'Mittel (8 CPU, 16 GiB RAM, 100 GiB)',
    profileLarge: 'Groß (16 CPU, 32 GiB RAM, 250 GiB)',
    profileCustom: 'Benutzerdefiniert',
    cpuLabel: 'CPU Kerne',
    cpuHint: 'Min: 1, Max: 64',
    memoryLabel: 'Arbeitsspeicher (GiB)',
    memoryHint: 'Min: 1, Max: 256 GiB',
    storageLabel: 'Speicher (GiB)',
    storageHint: 'Min: 10, Max: 1000 GiB',
    storageClassLabel: 'Speicherklasse',
    storageClassStandard: 'Standard (HDD)',
    storageClassPremium: 'Premium (SSD)',
    storageClassLocalPath: 'Local Path',
    errors: {
      cpuRange: 'CPU muss zwischen 1 und 64 Kernen liegen.',
      memoryRange: 'Arbeitsspeicher muss zwischen 1 und 256 GiB liegen.',
      storageRange: 'Speicher muss zwischen 10 und 1000 GiB liegen.',
      storageClassRequired: 'Speicherklasse ist erforderlich.',
    },
  },
  security: {
    networkTitle: 'Netzwerkrichtlinien',
    networkPolicyToggleLabel: 'NetworkPolicy aktivieren',
    networkPolicyAriaLabel: 'NetworkPolicy aktivieren oder deaktivieren',
    networkPolicyDescription: 'Schränkt den Netzwerkverkehr zwischen Pods auf erlaubte Verbindungen ein.',
    kyvernoTitle: 'Kyverno-Profil',
    kyvernoProfileLabel: 'Profil',
    kyvernoDescBaseline: 'Minimale Einschränkungen. Verhindert bekannte Privilegien-Eskalationen.',
    kyvernoDescRestricted: 'Strenge Einschränkungen nach Best Practices. Empfohlen für Produktion.',
    kyvernoDescCustom: 'Benutzerdefiniertes Profil mit individuellen Kyverno-Regeln.',
    backupTitle: 'Backup-Richtlinie',
    scheduleLabel: 'Zeitplan',
    scheduleDaily: 'Täglich',
    scheduleWeekly: 'Wöchentlich',
    scheduleCustom: 'Benutzerdefiniert (Cron)',
    retentionLabel: 'Aufbewahrung (Tage)',
    cronLabel: 'Cron-Ausdruck',
    cronHint: 'Format: Minute Stunde Tag Monat Wochentag (z.B. 0 2 * * *)',
    masterPasswordLabel: 'Master-Passwort-Secret (optional)',
    masterPasswordHint: 'Name eines vorhandenen Secrets, leer legt eines an.',
    errors: {
      cronInvalid: 'Gib einen gültigen Cron-Ausdruck ein (z.B. 0 2 * * *).',
      retentionRange: 'Aufbewahrungsdauer muss zwischen 1 und 365 Tagen liegen.',
    },
  },
  preview: {
    manifestTitle: 'OpenDeskTenant Manifest',
    linesSuffix: 'Zeilen',
    copy: 'Kopieren',
    copied: 'Kopiert!',
    copyAriaLabel: 'YAML in Zwischenablage kopieren',
    yamlAriaLabel: 'OpenDeskTenant YAML-Vorschau',
    createTenant: 'Mandant erstellen',
    createTenantAriaLabel: 'Mandant erstellen',
  },
  review: {
    basic: 'Grundeinstellungen',
    apps: 'Anwendungen',
    resources: 'Ressourcen',
    security: 'Sicherheit',
    edit: 'Bearbeiten',
    editAriaLabelSuffix: 'bearbeiten',
    tenantName: 'Mandantenname',
    domain: 'Domain',
    displayName: 'Anzeigename',
    edition: 'Edition',
    editionEnterprise: 'Enterprise',
    editionCommunity: 'Community',
    adminEmail: 'Admin-E-Mail',
    release: 'openDesk-Version',
    releaseAuto: 'Automatisch (neueste)',
    profile: 'Profil',
    cpu: 'CPU Kerne',
    memory: 'Arbeitsspeicher',
    storage: 'Speicher',
    storageClass: 'Speicherklasse',
    networkPolicy: 'NetworkPolicy',
    enabled: 'Aktiviert',
    disabled: 'Deaktiviert',
    kyvernoProfile: 'Kyverno-Profil',
    backupSchedule: 'Backup-Zeitplan',
    scheduleDaily: 'Täglich',
    scheduleWeekly: 'Wöchentlich',
    scheduleCronPrefix: 'Cron:',
    retention: 'Aufbewahrung',
    retentionDaysSuffix: 'Tage',
    enterprise: 'Enterprise',
    createTenant: 'Mandant erstellen',
    createTenantAriaLabel: 'Mandant erstellen',
  },
} as const;

export const en = {
  heading: 'Create new tenant',
  stepIndicatorAriaLabel: 'Wizard steps',
  contentAriaLabelDefault: 'Wizard step',
  unknownError: 'Unknown error during creation.',
  steps: {
    basic: 'Basic settings',
    apps: 'Applications',
    mail: 'Mail & MTA',
    iam: 'IAM',
    network: 'Network',
    storage: 'Storage',
    quota: 'Quota',
    security: 'Security',
    license: 'Licence',
    preview: 'Preview',
  },
  nav: {
    cancel: 'Cancel',
    back: 'Back',
    next: 'Next',
  },
  cancelModal: {
    title: 'Cancel the wizard?',
    body: 'Unsaved changes will be lost. Do you really want to cancel the wizard?',
    confirm: 'Yes, cancel',
  },
  progress: {
    steps: {
      namespace: 'Creating namespace',
      rbac: 'Configuring RBAC',
      networkPolicies: 'Applying NetworkPolicies',
      secrets: 'Creating secrets',
      keycloakClient: 'Registering Keycloak client',
      postgres: 'Creating PostgreSQL databases',
      redis: 'Provisioning Redis',
      nextcloud: 'Installing Nextcloud',
      oxAppSuite: 'Installing OX App Suite',
      elementMatrix: 'Installing Element/Matrix',
      openproject: 'Installing OpenProject',
      collabora: 'Installing Collabora',
      jitsi: 'Installing Jitsi',
      xwiki: 'Installing XWiki',
      ingress: 'Configuring ingress',
      tls: 'Issuing TLS certificates',
      activate: 'Activating tenant',
    },
    titlePrefix: 'Creating tenant:',
    badgeCompleted: 'Completed',
    badgeError: 'Error',
    badgePending: 'In progress',
    percentSuffix: '%',
    ariaLabelPercentPrefix: 'Provisioning progress:',
    timelineAriaLabel: 'Provisioning steps',
    errorLabel: 'Error:',
    completeMessage: 'The tenant was provisioned successfully.',
    retry: 'Retry',
    retrying: 'Retrying...',
    close: 'Close',
    viewTenant: 'View tenant',
    liveRegionCreated: 'Tenant created successfully.',
    liveRegionErrorPrefix: 'Error:',
    sseConnectionLost: 'Connection to the server was lost. Please try again.',
  },
  basicInfo: {
    sectionTitle: 'Basic settings',
    cluster: 'Cluster',
    tenantName: 'Tenant name',
    tenantNameHint: 'Lowercase letters, digits and hyphens (3-63 characters).',
    domain: 'Domain',
    domainHint: 'Fully qualified domain name (e.g. tenant.example.com).',
    displayName: 'Display name',
    displayNameHint: 'Optional display name for the tenant.',
    edition: 'Edition',
    editionAriaLabel: 'Select edition',
    editionCE: 'Community Edition (CE)',
    editionCEAriaLabel: 'Community Edition (CE)',
    editionCEDesc: 'Open-source components without enterprise features.',
    editionEE: 'Enterprise Edition (EE)',
    editionEEAriaLabel: 'Enterprise Edition (EE)',
    editionEEDesc: 'Extended features, SLA support and enterprise integrations.',
    adminEmail: 'Admin email',
    adminEmailHint: 'Email address of the tenant administrator.',
    release: 'openDesk version',
    releaseLoading: 'Loading releases...',
    errors: {
      tenantNameRequired: 'Tenant name is required.',
      tenantNameInvalid: 'Only lowercase letters, digits and hyphens (3-63 characters).',
      domainRequired: 'Domain is required.',
      domainInvalid: 'Please enter a valid FQDN.',
      adminEmailRequired: 'Admin email is required.',
      adminEmailInvalid: 'Please enter a valid email address.',
    },
  },
  iam: {
    keycloakTitle: 'Keycloak',
    keycloakDescription: 'Identity and access management configuration for the tenant.',
    ldapOU: 'LDAP OU',
    ldapOUHint: 'Organizational unit in the LDAP directory (empty = automatic).',
    ldapBaseDN: 'LDAP base DN (optional)',
    ldapBaseDNHint: 'Empty keeps the cluster default.',
    mfaTitle: 'Multi-factor authentication',
    mfaDescription: 'MFA methods enabled for this tenant\'s users.',
    mfaLegend: 'Select MFA methods',
    mfaGroupAriaLabel: 'MFA methods',
    mfaTotpDescription: 'Time-based one-time passwords (Google Authenticator, etc.).',
    mfaWebauthnDescription: 'FIDO2/WebAuthn hardware security keys.',
    mfaSmsDescription: 'One-time passwords via SMS (requires an SMS gateway).',
    errors: {
      ldapOUInvalid: String.raw`The LDAP OU may not contain special characters (,=+<>#;\").`,
    },
  },
  mail: {
    smtpTitle: 'SMTP configuration',
    smtpDescription: 'Configure the outgoing mail server for this tenant.',
    smtpHost: 'SMTP host',
    smtpHostHint: 'Hostname of the SMTP server (e.g. smtp.example.com).',
    smtpPort: 'SMTP port',
    smtpPortHint: 'Default: 587 (STARTTLS) or 465 (TLS).',
    credentialRef: 'Credential secret',
    credentialRefHint: 'Name of the Kubernetes secret holding SMTP credentials (optional).',
    dkimTitle: 'DKIM',
    dkimDescription: 'DomainKeys Identified Mail for email authentication.',
    dkimSelector: 'DKIM selector',
    dkimSelectorHint: 'DNS selector for the DKIM signature (e.g. default).',
    errors: {
      smtpHostInvalid: 'Please enter a valid hostname.',
      smtpPortInvalid: 'Port must be between 1 and 65535.',
    },
  },
  network: {
    isolationTitle: 'Network isolation',
    isolationDescription: 'Sets how strictly network traffic between tenants is isolated.',
    isolationLegend: 'Isolation mode',
    isolationAriaLabel: 'Select isolation mode',
    isolationStrict: 'Strict',
    isolationStrictAriaLabel: 'Strict',
    isolationStrictDesc: 'No network traffic between tenant namespaces. Recommended for production.',
    isolationPermissive: 'Permissive',
    isolationPermissiveAriaLabel: 'Permissive',
    isolationPermissiveDesc: 'Network traffic between tenant namespaces allowed. Development only.',
    webrtcTitle: 'WebRTC servers',
    webrtcDescription: 'STUN/TURN servers for Jitsi video conferencing and real-time communication.',
    stunServer: 'STUN server',
    stunServerHint: 'e.g. stun:stun.example.com:3478',
    turnServer: 'TURN server',
    turnServerHint: 'e.g. turn:turn.example.com:3478',
    errors: {
      stunInvalid: 'Please enter a valid STUN server address.',
      turnInvalid: 'Please enter a valid TURN server address.',
    },
  },
  storage: {
    s3Title: 'Object storage (S3)',
    s3Description: 'S3-compatible storage configuration per application.',
    appNextcloud: 'Nextcloud',
    appNextcloudDesc: 'File storage and collaboration.',
    appMigrations: 'Migrations',
    appMigrationsDesc: 'Backup and migration data.',
    bucket: 'Bucket',
    bucketHintPrefix: 'S3 bucket for',
    endpoint: 'Endpoint',
    endpointHint: 'S3 API endpoint (e.g. s3.example.com).',
    useSSL: 'Use SSL',
    useSSLAriaLabelPrefix: 'Use SSL for',
    useSSLAriaLabelSuffix: '',
    pathStyle: 'Path style',
    pathStyleAriaLabelPrefix: 'Use path style for',
    pathStyleAriaLabelSuffix: '',
    dbTitle: 'Database',
    dbDescription: 'PostgreSQL connection configuration for the tenant.',
    dbHost: 'Database host',
    dbHostHint: 'PostgreSQL host (e.g. postgresql.opendesk.svc:5432).',
    dbSecret: 'Database secret',
    dbSecretHint: 'Kubernetes secret holding database credentials.',
    errors: {
      endpointInvalid: 'Please enter a valid endpoint.',
      dbHostInvalid: 'Please enter a valid database hostname.',
    },
  },
  quota: {
    resourcesTitle: 'Resource quota',
    resourcesDescription: 'Maximum resources allocatable to this tenant.',
    cpu: 'CPU cores',
    cpuHint: '1 - 256 cores',
    memory: 'Memory (GiB)',
    memoryHint: '1 - 1024 GiB',
    storage: 'Storage (GiB)',
    storageHint: '10 - 10,000 GiB',
    pods: 'Max. pods',
    podsHint: '10 - 10,000',
    replicasTitle: 'Replicas per chart',
    replicasDescription: 'Override the default replica count for individual Helm charts.',
    chart: 'Chart',
    chartPlaceholder: 'Select chart...',
    replicas: 'Replicas',
    removeEntryPrefix: 'Remove replica entry',
    removeEntrySuffix: '',
    remove: 'Remove',
    addReplica: '+ Replica entry',
    errors: {
      cpuRange: 'CPU must be between 1 and 256 cores.',
      memoryRange: 'Memory must be between 1 and 1024 GiB.',
      storageRange: 'Storage must be between 10 and 10,000 GiB.',
      podsRange: 'Pod limit must be between 10 and 10,000.',
      chartRequired: 'Chart name is required.',
      replicasRange: 'Replicas must be between 0 and 99.',
    },
  },
  apps: {
    legend: 'Select components',
    selectAll: 'Select all',
    deselectAll: 'Deselect all',
    groupAriaLabel: 'Release components',
    loading: 'Loading components ...',
    loadError: 'Could not load the components.',
    errors: {
      apps: 'Enable at least one component.',
    },
  },
  resources: {
    profileLabel: 'Scaling profile',
    profileSmall: 'Small (4 CPU, 8 GiB RAM, 50 GiB)',
    profileMedium: 'Medium (8 CPU, 16 GiB RAM, 100 GiB)',
    profileLarge: 'Large (16 CPU, 32 GiB RAM, 250 GiB)',
    profileCustom: 'Custom',
    cpuLabel: 'CPU cores',
    cpuHint: 'Min: 1, Max: 64',
    memoryLabel: 'Memory (GiB)',
    memoryHint: 'Min: 1, Max: 256 GiB',
    storageLabel: 'Storage (GiB)',
    storageHint: 'Min: 10, Max: 1000 GiB',
    storageClassLabel: 'Storage class',
    storageClassStandard: 'Standard (HDD)',
    storageClassPremium: 'Premium (SSD)',
    storageClassLocalPath: 'Local Path',
    errors: {
      cpuRange: 'CPU must be between 1 and 64 cores.',
      memoryRange: 'Memory must be between 1 and 256 GiB.',
      storageRange: 'Storage must be between 10 and 1000 GiB.',
      storageClassRequired: 'Storage class is required.',
    },
  },
  security: {
    networkTitle: 'Network policies',
    networkPolicyToggleLabel: 'Enable NetworkPolicy',
    networkPolicyAriaLabel: 'Enable or disable NetworkPolicy',
    networkPolicyDescription: 'Restricts network traffic between pods to allowed connections.',
    kyvernoTitle: 'Kyverno profile',
    kyvernoProfileLabel: 'Profile',
    kyvernoDescBaseline: 'Minimal restrictions. Prevents known privilege escalations.',
    kyvernoDescRestricted: 'Strict restrictions following best practices. Recommended for production.',
    kyvernoDescCustom: 'Custom profile with individual Kyverno rules.',
    backupTitle: 'Backup policy',
    scheduleLabel: 'Schedule',
    scheduleDaily: 'Daily',
    scheduleWeekly: 'Weekly',
    scheduleCustom: 'Custom (cron)',
    retentionLabel: 'Retention (days)',
    cronLabel: 'Cron expression',
    cronHint: 'Format: minute hour day month weekday (e.g. 0 2 * * *).',
    masterPasswordLabel: 'Master password Secret (optional)',
    masterPasswordHint: 'Name of an existing Secret, empty creates one.',
    errors: {
      cronInvalid: 'Enter a valid cron expression (e.g. 0 2 * * *).',
      retentionRange: 'Retention must be between 1 and 365 days.',
    },
  },
  preview: {
    manifestTitle: 'OpenDeskTenant manifest',
    linesSuffix: 'lines',
    copy: 'Copy',
    copied: 'Copied!',
    copyAriaLabel: 'Copy YAML to clipboard',
    yamlAriaLabel: 'OpenDeskTenant YAML preview',
    createTenant: 'Create tenant',
    createTenantAriaLabel: 'Create tenant',
  },
  review: {
    basic: 'Basic settings',
    apps: 'Applications',
    resources: 'Resources',
    security: 'Security',
    edit: 'Edit',
    editAriaLabelSuffix: '',
    tenantName: 'Tenant name',
    domain: 'Domain',
    displayName: 'Display name',
    edition: 'Edition',
    editionEnterprise: 'Enterprise',
    editionCommunity: 'Community',
    adminEmail: 'Admin email',
    release: 'openDesk version',
    releaseAuto: 'Automatic (latest)',
    profile: 'Profile',
    cpu: 'CPU cores',
    memory: 'Memory',
    storage: 'Storage',
    storageClass: 'Storage class',
    networkPolicy: 'NetworkPolicy',
    enabled: 'Enabled',
    disabled: 'Disabled',
    kyvernoProfile: 'Kyverno profile',
    backupSchedule: 'Backup schedule',
    scheduleDaily: 'Daily',
    scheduleWeekly: 'Weekly',
    scheduleCronPrefix: 'Cron:',
    retention: 'Retention',
    retentionDaysSuffix: 'days',
    enterprise: 'Enterprise',
    createTenant: 'Create tenant',
    createTenantAriaLabel: 'Create tenant',
  },
} as const;
